import React, { useEffect, useRef } from "react";
import { EnvironmentSelector } from "./RevenueAnalytics/Elements/EnvironmentSelection";

const MAX_ENV_NAME_LEN = 12;
const ENV_SHORTENINGS: [string, string][] = [
    ["NonProduction", "NonProd"],
    ["Non Production", "NonProd"],
    ["Production", "Prod"],
];

function shortenEnvName(name: string): string {
    if (name.length <= MAX_ENV_NAME_LEN) return name;
    let s = name;
    for (const [full, short] of ENV_SHORTENINGS) {
        s = s.replace(new RegExp(full.replace(/\s/g, "\\s"), "gi"), short);
    }
    if (s.length <= MAX_ENV_NAME_LEN) return s;
    return s.slice(0, MAX_ENV_NAME_LEN - 2) + "..";
}

function processEnvNameTruncation(
    container: HTMLElement,
    scheduledRef: React.MutableRefObject<boolean>
) {
    if (scheduledRef.current) return;
    const isInDropdown = (el: Element) => el.closest("ul.absolute");
    const button = container.querySelector("button");
    if (!button) return;

    const toProcess: { el: HTMLElement; full: string }[] = [];

    container.querySelectorAll("[data-env-name]").forEach((node) => {
        const el = node as HTMLElement;
        if (isInDropdown(el)) return;
        const raw = el.getAttribute("data-original-env") || el.textContent || "";
        const full = el.tagName === "DIV" ? raw.trim() : raw;
        if (full) toProcess.push({ el, full });
    });

    if (toProcess.length === 0) return;

    scheduledRef.current = true;

    toProcess.forEach(({ el, full }) => {
        el.textContent = full;
        el.removeAttribute("data-original-env");
        el.removeAttribute("title");
    });

    const overflow = button.scrollWidth > button.clientWidth;

    toProcess.forEach(({ el, full }) => {
        if (overflow && full.length > MAX_ENV_NAME_LEN) {
            el.setAttribute("data-original-env", full);
            el.setAttribute("title", full);
            el.textContent = shortenEnvName(full);
        }
    });

    requestAnimationFrame(() => {
        scheduledRef.current = false;
    });
}

export const EnvSelectorWithTruncation = () => {
    const containerRef = useRef<HTMLDivElement>(null);
    const scheduledRef = useRef(false);
    const isTruncatingRef = useRef(false);

    useEffect(() => {
        const el = containerRef.current;
        if (!el) return;

        let debounceTimer: ReturnType<typeof setTimeout>;
        let guardTimer: ReturnType<typeof setTimeout>;

        const run = () => {
            if (isTruncatingRef.current) return;
            isTruncatingRef.current = true;
            processEnvNameTruncation(el, scheduledRef);
            // Keep guard up long enough to ignore observer events from our own DOM mutations
            guardTimer = setTimeout(() => {
                isTruncatingRef.current = false;
            }, 100);
        };

        const debouncedRun = () => {
            if (isTruncatingRef.current) return;
            clearTimeout(debounceTimer);
            debounceTimer = setTimeout(run, 50);
        };

        // Run when EnvironmentSelector content changes (async data loaded)
        const observer = new MutationObserver(debouncedRun);
        observer.observe(el, { childList: true, subtree: true, characterData: true });

        // Fallback: run at fixed intervals in case MutationObserver misses updates
        const t1 = setTimeout(run, 600);
        const t2 = setTimeout(run, 1500);
        const t3 = setTimeout(run, 3000);

        const onResize = () => run();
        window.addEventListener("resize", onResize);

        return () => {
            observer.disconnect();
            clearTimeout(debounceTimer);
            clearTimeout(guardTimer);
            clearTimeout(t1);
            clearTimeout(t2);
            clearTimeout(t3);
            window.removeEventListener("resize", onResize);
        };
    }, []);

    return (
        <div ref={containerRef} className="flex flex-col gap-2 w-full">
            <EnvironmentSelector />
        </div>
    );
};
